import type { AgentCard } from '@ralph/contracts'
import clsx from 'clsx'
import { ArrowRight, Check } from 'lucide-react'
import { type CSSProperties, type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { AgentAvatar } from '@/components/marketplace/consumer/AgentAvatar.tsx'
import { OfficialMark } from '@/components/marketplace/consumer/AgentBadges.tsx'
import { ownerName } from '@/components/marketplace/consumer/agent-identity.ts'
import { Overlay } from '@/components/ui/Overlay.tsx'
import { useT } from '@/i18n/index.ts'

/**
 * Which agent answers — the one control the product has for that choice, in both places it is
 * made: the home composer, before a conversation exists, and the chat header, where picking
 * another one starts a new conversation (NAV-030).
 *
 * **Why it is built rather than a native `<select>`.** The old home picker was a transparent
 * `<select>` over a pill, and its reasons were good ones: the platform menu is keyboard-complete
 * and it is the only version that behaves on a phone. It cannot render the thing that makes an
 * agent recognisable, though — the identity tile, the Official mark, what the agent is for — and
 * a list of bare names is precisely the wrong surface for a decision that cannot be undone
 * (AGM-007: the pin is made at creation and the conversation can never be repointed). So the
 * keyboard contract the platform would have given us is written out below instead.
 *
 * **Why it portals.** The chat header lives inside `<section class="glass … overflow-clip">`.
 * An absolutely positioned panel there is clipped at the panel's edge, and `position: fixed`
 * does not escape either — `backdrop-filter` on `.glass` makes that element the containing block
 * for every fixed descendant, which is the trap `Overlay.tsx` exists to document. The menu
 * therefore renders into `document.body` and positions itself from the trigger's measured rect.
 *
 * **Why `menu`/`menuitemradio` and not `listbox` or `dialog`.** The reader is choosing one of a
 * set, inside a menu, and activating a choice closes it — which is what `menuitemradio` means.
 * `role="dialog"` is unavailable: the builder's browser journey counts dialogs on the page
 * unqualified, and two mounted ones is a broken page rather than a layered one. `listbox` would
 * be defensible on home and a lie in the header, where choosing navigates.
 *
 * The trigger's *contents* belong to the caller. The two sites draw genuinely different things —
 * a mono chip on the composer, an identity line with an eyebrow and a trust mark in the header —
 * and the alternative is a component with six presentation props, which is how two surfaces end
 * up disagreeing about what the same control looks like.
 *
 * **It opens even when there is only one agent to list, and that reverses the rule this
 * component shipped with.** The first version drew a plain label instead of a control whenever
 * there was nothing to switch to, on the reasoning that a menu with one option is a menu that
 * does nothing. What that missed is the state a deployment is actually in: a fresh install has
 * one published agent, so the rule made the control permanently inert on the screen where it
 * matters most — and a pill carrying an identity tile reads as pressable whether or not it is.
 * A one-item list is not a dead end either, because the last item is the way to the gallery:
 * "you have one agent, and here is where the others come from" is a useful answer, and an
 * unresponsive pill is not an answer at all.
 */

type AgentPickerProps = {
  /** Every agent this viewer can start a conversation with, default first (AGM-004). */
  agents: AgentCard[]
  /**
   * The agent to mark as current: what the composer has chosen, or what the open conversation
   * is pinned to. May be an agent that is not in `agents` at all — a listing since retired —
   * in which case nothing is marked.
   */
  currentId: string | null | undefined
  onPick: (agentId: string) => void
  /** What the trigger looks like. Rendered inside the button this component owns. */
  trigger: ReactNode
  triggerClassName?: string
  /** The trigger's accessible name. The visible contents are usually an agent's name alone. */
  triggerLabel: string
  /** The menu's heading, and the sentence under it that says what picking one will do. */
  title: string
  hint: string
  /** What marks the current row — "chosen" on home, "this conversation" in the header. */
  currentLabel: string
  /** Which trigger edge the panel aligns to. The header pill sits at the right of the top bar. */
  align?: 'start' | 'end'
}

/** Room the panel insists on having below the trigger before it decides to open upwards. */
const MIN_ROOM_BELOW = 220
const GAP = 8
const MARGIN = 12
const WIDTH = 328

export const AgentPicker = ({
  agents,
  currentId,
  onPick,
  trigger,
  triggerClassName,
  triggerLabel,
  title,
  hint,
  currentLabel,
  align = 'start'
}: AgentPickerProps) => {
  const id = useId()
  const menuId = `${id}-menu`
  const titleId = `${id}-title`
  const navigate = useNavigate()

  // The two words this menu owns — the rest of its prose arrives as props from the surface
  // that opened it. Read in the body so a locale switch reaches them (PRD-27 F-3, LOC-012).
  const t = useT().marketplace.gallery
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<CSSProperties | null>(null)
  const [active, setActive] = useState(0)

  /*
   * The agents, plus the way to the rest of them.
   *
   * The gallery link is a menu item rather than a footer under the list, and that is an
   * accessibility decision rather than a layout one: Tab closes this menu (it is a menu, not a
   * modal), so anything focusable outside the `role="menu"` element would be unreachable from
   * the keyboard. As an item it joins the roving focus and the arrow keys walk onto it.
   */
  const stops = agents.length + 1
  const browseIndex = agents.length

  const close = (returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }

  /*
   * Measured at the moment of opening rather than tracked.
   *
   * A panel that follows its trigger needs either a resize observer on an ancestor chain it
   * does not own, or a scroll listener that repositions on every frame. Both are more machinery
   * than a menu deserves; closing on scroll and on resize (below) is the behaviour a reader
   * already expects from every other menu they use, and it can never end up pinned to a
   * trigger that has moved out from under it.
   */
  const openAt = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return

    const width = Math.min(WIDTH, window.innerWidth - MARGIN * 2)
    const edge = align === 'end' ? rect.right - width : rect.left
    const left = Math.min(Math.max(MARGIN, edge), Math.max(MARGIN, window.innerWidth - width - MARGIN))

    const below = window.innerHeight - rect.bottom - GAP - MARGIN
    const above = rect.top - GAP - MARGIN
    const upwards = below < MIN_ROOM_BELOW && above > below

    setPosition({
      position: 'fixed',
      left,
      width,
      maxHeight: Math.max(160, upwards ? above : below),
      ...(upwards ? { bottom: window.innerHeight - rect.top + GAP } : { top: rect.bottom + GAP })
    })
    // Open on the current agent, so the first arrow press moves from where the reader is
    // rather than from the top of a list they have not read.
    setActive(
      Math.max(
        0,
        agents.findIndex((agent) => agent.agentId === currentId)
      )
    )
    setOpen(true)
  }

  // Roving focus. The active row is the only one in the tab order, and focus follows it, which
  // is what makes the arrow keys announce each agent as they pass it. The selector takes both
  // item roles, so the gallery link at the end is one more stop rather than a special case.
  useEffect(() => {
    if (!open) return
    const rows = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"], [role="menuitem"]')
    rows?.[active]?.focus()
  }, [open, active])

  /*
   * Dismissal that is not a key press: a click elsewhere, a scroll, a resize.
   *
   * `mousedown` rather than `click` so the menu is gone before whatever was clicked reacts, and
   * the trigger is excluded from it — otherwise pressing the open trigger closes the menu here
   * and its own handler immediately reopens it, which reads as a menu that cannot be closed.
   */
  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setOpen(false)
    }

    const onScroll = (event: Event) => {
      // A scroll inside the menu's own list is not a dismissal.
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return
      setOpen(false)
    }

    // The panel is placed from a rect measured on open, so a viewport that changes size has
    // invalidated it. Closing is the honest response; re-measuring would leave a menu open
    // across a rotation that may have moved its trigger to another part of the layout entirely.
    const onResize = () => setOpen(false)

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    // Captured, because the surfaces that scroll in this app are inner panels rather than the
    // document, and a scroll event on one of those does not bubble to the window.
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open])

  const onMenuKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      close(true)
      return
    }
    // Tab leaves the menu rather than cycling inside it: this is a menu, not a modal, and a
    // reader who tabs is done with it. Focus goes back to the trigger first so the browser
    // continues from where they were, not from the top of the document.
    if (event.key === 'Tab') {
      close(true)
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => (index + 1) % stops)
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => (index - 1 + stops) % stops)
      return
    }
    if (event.key === 'Home') {
      event.preventDefault()
      setActive(0)
      return
    }
    if (event.key === 'End') {
      event.preventDefault()
      setActive(stops - 1)
    }
  }

  const choose = (agentId: string) => {
    setOpen(false)
    triggerRef.current?.focus()
    onPick(agentId)
  }

  // Focus is NOT handed back to the trigger here: the trigger is about to leave with the page.
  const browse = () => {
    setOpen(false)
    navigate('/agents')
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(true) : openAt())}
        onKeyDown={(event) => {
          // Down-arrow opens onto the list, the one menu convention a plain button does not
          // give for free.
          if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault()
            openAt()
          }
        }}
        className={triggerClassName}
      >
        {trigger}
      </button>

      {open && position && (
        <Overlay>
          <div
            ref={menuRef}
            style={position}
            className="fade-in z-50 flex flex-col overflow-clip rounded-panel border border-rule bg-card-solid shadow-frame"
          >
            {/*
              The heading and the hint sit OUTSIDE the `menu` element. Non-interactive content
              inside one is not allowed to be there, and a screen reader walking a menu should
              hear options rather than prose — so the menu is labelled by the heading instead.
            */}
            <div className="border-b border-rule px-3.5 pt-3 pb-2.5">
              <p id={titleId} className="text-[13px] leading-snug font-semibold tracking-[-0.014em]">
                {title}
              </p>
              <p className="mt-1 text-pretty text-[12px] leading-[1.45] text-fg-muted">{hint}</p>
            </div>

            {/*
              The keys are bound to the `menu` rather than to the card around it. Every focus
              stop inside the panel is a `menuitemradio` in here, so the handler sees all of
              them by bubbling — and a keyboard contract that belongs to a menu should be
              attached to the element that says it is one.
            */}
            <div
              role="menu"
              aria-labelledby={titleId}
              id={menuId}
              onKeyDown={onMenuKeyDown}
              className="min-h-0 flex-1 overflow-y-auto p-1.5"
            >
              {agents.map((agent, index) => {
                const current = agent.agentId === currentId
                return (
                  <button
                    key={agent.agentId}
                    type="button"
                    role="menuitemradio"
                    aria-checked={current}
                    tabIndex={index === active ? 0 : -1}
                    onClick={() => choose(agent.agentId)}
                    onMouseMove={() => setActive(index)}
                    className={clsx(
                      // `focus-outline`: the panel clips its content, so the app's spread focus
                      // ring is cut off at the row edge and reads as a divider rather than as
                      // focus.
                      'focus-outline flex w-full items-start gap-2.5 rounded-chip px-2 py-2 text-left transition-colors duration-150',
                      index === active ? 'bg-card' : 'hover:bg-card'
                    )}
                  >
                    <AgentAvatar
                      displayName={agent.displayName}
                      icon={agent.icon}
                      accent={agent.accent}
                      size="sm"
                      className="mt-[1px]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                        <span className="truncate text-[13px] leading-snug font-semibold tracking-[-0.014em]">
                          {agent.displayName}
                        </span>
                        {agent.official && <OfficialMark />}
                      </span>
                      {agent.tagline && (
                        <span className="mt-0.5 line-clamp-2 block text-pretty text-[12px] leading-[1.4] text-fg-muted">
                          {agent.tagline}
                        </span>
                      )}
                      {/* Whose it is — a person, or a group with its avatar (PRD-26 D-19) — the
                          same line the gallery card carries. */}
                      <span className="readout mt-1 flex min-w-0 items-center gap-1 text-fg-subtle">
                        {agent.owner.kind === 'group' && <GroupAvatar group={agent.owner.group} size={16} />}
                        <span className="min-w-0 truncate">{t.card.ownerLine(ownerName(agent.owner))}</span>
                      </span>
                      {/* The marker is a word as well as a tick: colour and shape are never the
                          only carriers of meaning here (AGM-124), and "this conversation" says
                          something a tick beside a name cannot. */}
                      {current && <span className="eyebrow mt-1 block text-primary">{currentLabel}</span>}
                    </span>
                    {current && (
                      <Check size={13} strokeWidth={2.6} className="mt-1 shrink-0 text-primary" aria-hidden />
                    )}
                  </button>
                )
              })}

              {/*
                The way to the rest of them, and the reason a one-agent menu is still worth
                opening. It sits under a rule rather than in a footer of its own, because a
                focusable control outside the `menu` element cannot be reached: Tab closes this.
              */}
              <div aria-hidden className="my-1.5 h-px bg-rule" />
              <button
                type="button"
                role="menuitem"
                tabIndex={browseIndex === active ? 0 : -1}
                onClick={browse}
                onMouseMove={() => setActive(browseIndex)}
                className={clsx(
                  'focus-outline flex w-full items-center gap-2 rounded-chip px-2 py-[7px] text-left',
                  'text-[12.5px] tracking-[-0.012em] transition-colors duration-150',
                  browseIndex === active ? 'bg-card text-fg' : 'text-fg-muted hover:bg-card hover:text-fg'
                )}
              >
                {t.picker.browse}
                <ArrowRight size={12} strokeWidth={2.2} className="ml-auto shrink-0" aria-hidden />
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </>
  )
}
